/**
 * HTML delle email (arrivano da mittenti esterni) ridotto a una whitelist di tag di formattazione,
 * e separato in testo nuovo e testo citato (risposte/inoltri precedenti).
 *
 * Il parsing avviene con DOMParser in un documento inerte: niente script eseguiti ne' immagini
 * scaricate. Poi si ricostruisce un DOM nuovo copiando solo i tag ammessi, senza attributi,
 * tranne href dei link (solo http/https/mailto). Tutto il resto viene tolto, tenendone il testo.
 */
const TAG_AMMESSI = new Set([
	'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr',
]);
// eliminati con tutto il contenuto
const TAG_RIMOSSI = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'head', 'title', 'meta', 'link', 'svg', 'math', 'form', 'button', 'input', 'select', 'textarea']);
const HREF_AMMESSO = /^(https?:|mailto:)/i;

// inizio del testo citato: "Il giorno ... ha scritto:", "On ... wrote:", inoltri, intestazioni Outlook
const INIZIO_CITAZIONE = [
	/^-{2,}\s*(forwarded message|messaggio inoltrato|original message|messaggio originale)/i,
	/^(il giorno|in data)\b.{0,200}\b(ha scritto|scrive)\s*:?\s*$/i,
	/^on\b.{0,200}\bwrote\s*:?\s*$/i,
	/^le\b.{0,200}\ba [ée]crit\s*:?\s*$/i,
	// citazione in testo semplice ("> riga")
	/^>/,
	/^el\b.{0,200}\bescribi[óo]\s*:?\s*$/i,
	/^am\b.{0,200}\bschrieb\b.{0,100}:?\s*$/i,
	/^(da|from|de|von)\s*:\s*\S.{0,200}$/i,
	/^-{2,}\s*(mensaje original|message d'origine|urspr[üu]ngliche nachricht)/i,
	/^_{8,}/,
];

function copia(nodo, doc) {
	if (nodo.nodeType === 3) return doc.createTextNode(nodo.nodeValue);
	if (nodo.nodeType !== 1) return null;
	const tag = nodo.tagName.toLowerCase();
	if (TAG_RIMOSSI.has(tag)) return null;
	const figli = Array.from(nodo.childNodes)
		.map(f => copia(f, doc))
		.filter(Boolean);
	if (!TAG_AMMESSI.has(tag)) {
		// tag non ammesso (img, font, center...): resta solo il contenuto
		const frammento = doc.createDocumentFragment();
		figli.forEach(f => frammento.appendChild(f));
		return frammento;
	}
	const el = doc.createElement(tag);
	if (tag === 'a') {
		const href = (nodo.getAttribute('href') || '').trim();
		if (HREF_AMMESSO.test(href)) {
			el.setAttribute('href', href);
			el.setAttribute('target', '_blank');
			el.setAttribute('rel', 'noopener noreferrer nofollow');
		}
	}
	figli.forEach(f => el.appendChild(f));
	return el;
}

function eCitazione(el) {
	if (el.nodeType !== 1) return false;
	if (el.tagName.toLowerCase() === 'blockquote') return true;
	return eRigaCitazione(el.textContent);
}

// spazi normalizzati: i client mettono spazi non separabili ("a&nbsp;écrit")
function eRigaCitazione(testo) {
	const riga = String(testo || '')
		.trim()
		.split('\n')[0]
		.replace(/\s+/g, ' ')
		.trim();
	return INIZIO_CITAZIONE.some(re => re.test(riga));
}

// "Grazie<br>Le 11 janv. ... a écrit :<br>..." nello stesso paragrafo: si divide il paragrafo alla
// riga che apre la citazione, cosi' il testo nuovo resta separato da quello citato.
// Il documento e' inerte (DOMParser): innerHTML qui non esegue niente.
function dividiAllaCitazione(body) {
	Array.from(body.children).forEach(el => {
		if (!/<br\s*\/?>/i.test(el.innerHTML)) return;
		const righe = el.innerHTML.split(/<br\s*\/?>/i);
		const k = righe.findIndex((r, i) => {
			if (i === 0) return false;
			return eRigaCitazione(new DOMParser().parseFromString(r, 'text/html').body.textContent);
		});
		if (k < 1) return;
		const seconda = el.ownerDocument.createElement(el.tagName);
		seconda.innerHTML = righe.slice(k).join('<br>');
		el.innerHTML = righe.slice(0, k).join('<br>');
		el.after(seconda);
	});
}

/**
 * @returns {{ corpo: string, citato: string }} HTML sicuro del messaggio nuovo e di quello citato
 */
export function pulisciEmail(html) {
	if (!html) return { corpo: '', citato: '' };
	const parser = new DOMParser();
	// testo semplice (senza tag): le righe vanno a capo
	const sorgente = /<[a-z][\s\S]*>/i.test(html) ? String(html) : String(html).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>');
	const inerte = parser.parseFromString(sorgente, 'text/html');
	dividiAllaCitazione(inerte.body);
	const doc = document.implementation.createHTMLDocument('');
	const corpo = doc.createElement('div');
	const citato = doc.createElement('div');

	let destinazione = corpo;
	Array.from(inerte.body.childNodes).forEach(nodo => {
		const pulito = copia(nodo, doc);
		if (!pulito) return;
		// dal primo blocco citato in poi e' tutto testo delle email precedenti
		if (destinazione === corpo && corpo.textContent.trim() && eCitazione(nodo)) {
			destinazione = citato;
			// la riga di attribuzione ("Le ... a écrit :") spezzata su piu' righe resta prima della citazione
			const prima = corpo.lastChild;
			const testo = prima ? (prima.textContent || '').replace(/\s+/g, ' ').trim() : '';
			if (testo.endsWith(':') && testo.length < 300 && corpo.childNodes.length > 1) citato.appendChild(prima);
		}
		destinazione.appendChild(pulito);
	});
	// paragrafi vuoti in coda (firme con righe bianche)
	[corpo, citato].forEach(box => {
		while (box.lastChild && !(box.lastChild.textContent || '').trim()) box.removeChild(box.lastChild);
	});
	return { corpo: corpo.innerHTML, citato: citato.innerHTML };
}

/** Testo con entita' HTML (es. "Ga&euml;tan" salvato cosi' nel database) in testo semplice. */
export function testoDaEntita(valore) {
	if (!valore || !/&[#a-z0-9]+;/i.test(valore)) return valore || '';
	return new DOMParser().parseFromString(String(valore), 'text/html').body.textContent || '';
}

/** Anteprima in testo semplice (lista): prime parole del messaggio nuovo. */
export function anteprimaEmail(html, max = 140) {
	if (!html) return '';
	// spazio dopo a capo e fine blocco, altrimenti "<p>Ciao,</p><p>hai" diventa "Ciao,hai"
	const spaziato = String(html).replace(/<(br|\/p|\/div|\/li|\/tr|\/td|\/h\d)\b[^>]*>/gi, '$& ');
	const inerte = new DOMParser().parseFromString(spaziato, 'text/html');
	inerte.querySelectorAll('script, style').forEach(n => n.remove());
	const testo = (inerte.body.textContent || '').replace(/\s+/g, ' ').trim();
	return testo.length > max ? `${testo.slice(0, max).trimEnd()}…` : testo;
}

export default pulisciEmail;
